import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { ISharedDataState } from '../../../services/facades/master-data/shared-data-state.model';
import { SharedDataFacade } from '../../../services/facades/master-data/shared-data.facade';
import { MeasureUnitDialogComponent } from './measure-unit-dialog/measure-unit-dialog.component';
import { map } from 'rxjs/operators';
import { IMeasureUnitState } from '../../../services/facades/master-data/measure-unit.state.model';
import { distinctUntilChanged, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { IMeasureUnit } from '../../../models/measure-unit.model';
import { MeasureUnitFacade } from '../../../services/facades/master-data/measure-unit.facade';
import { ConfirmDialogComponent } from '@sla-ng/ui-material';
import { MatSort } from '@angular/material/sort';

@Component({
    templateUrl: './measure-unit.page.html',
    styleUrls: [ './measure-unit.page.css' ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeasureUnitPage implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild(MatPaginator)
    public paginator: MatPaginator;
    @ViewChild(MatSort)
    public sort: MatSort;

    public dataSource: MatTableDataSource<IMeasureUnit> = new MatTableDataSource<IMeasureUnit>([]);
    public measureUnitState$: Observable<IMeasureUnitState>;
    public sharedDataState$: Observable<ISharedDataState>;
    private destroy$: Subject<void> = new Subject();


    constructor(private myMeasureUnitFacade: MeasureUnitFacade,
                private mySharedDataFacade: SharedDataFacade,
                private myMatDialog: MatDialog) {
    }

    public ngOnInit(): void {
        this.measureUnitState$ = this.myMeasureUnitFacade.subState();
        this.sharedDataState$ = this.mySharedDataFacade.subState();
        this.sharedDataState$
            .pipe(
                takeUntil(this.destroy$),
                map((s: ISharedDataState) => s.measureUnitLookup),
                distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: Record<number, IMeasureUnit>) => {
                this.dataSource.data = Object.values(data);
            });
        void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits());
    }

    public ngAfterViewInit(): void {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.myMeasureUnitFacade.clearState();
    }

    public onOpenDialog(measureUnit?: IMeasureUnit): void {
        const dialog: MatDialogRef<MeasureUnitDialogComponent> = this.myMatDialog.open(MeasureUnitDialogComponent, {
            width: '600px',
            data: isNullOrUndefined(measureUnit) ? {} : measureUnit
        });
        dialog.afterClosed().subscribe((update: boolean) => {
            if (update) {
                void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits(true));
            }
        });
    }

    public onDeleteMeasureUnit(measureUnit: IMeasureUnit): void {
        const dialog: MatDialogRef<ConfirmDialogComponent> = this.myMatDialog.open(ConfirmDialogComponent, {
            width: '600px',
            data: {
                message: 'app.page.measure-unit.confirm-delete.label',
                messageParams: { name: measureUnit.name }
            },
        });
        dialog.afterClosed().subscribe(async (isConfirmed: boolean) => {
            if (isConfirmed) {
                await this.myMeasureUnitFacade.deleteMeasureUnit(measureUnit.id);
                void firstValueFrom(this.mySharedDataFacade.fetchMeasureUnits(true));
            }
        });
    }

    public applyFilter(filterValue: string): void {
        filterValue = filterValue.trim(); // Remove whitespace
        filterValue = filterValue.toLowerCase(); // MatTableDataSource defaults to lowercase matches
        this.dataSource.filter = filterValue;
    }

}
